// =============================================================================
// app/src/lib/lerDas.ts — ler o DAS (e outra guia de convênio) pelo TEXTO.
//
// Pedido do dono (28/09/2026): "em contas a pagar, gostaria de poder enviar
// esse tipo de arquivo e ele autocompletar também".
//
// POR QUE ISTO NÃO É O LEITOR DE BOLETO. São duas mídias diferentes no mesmo
// envelope .pdf. A digitalização do CamScanner é uma FOTO: o dado se perdeu na
// tinta e no vinco, e `lerBoleto.ts` o recupera por votação de pixels. O DAS da
// Receita é TEXTO comprimido: o dado está lá, exato, e só precisa ser lido.
// Rasterizar um para reler com decodificador de barras seria transformar dado
// exato em imagem para adivinhar de volta.
//
// E ISTO TAMBÉM NÃO É UM EXTRATOR DE PDF. Não se lê um único operador de
// posição (Tm, Td, TD, T*), não se remonta linha nem coluna, não se consulta
// largura de glifo — é exatamente aí que leitor de PDF escrito à mão quebra.
// O que se faz é juntar os literais de texto numa SOPA de caracteres e varrer
// três padrões: a corrida de 48 dígitos, a data e o dinheiro.
//
// A ACEITAÇÃO NÃO É PALPITE, E É O QUE SEGURA TUDO. Uma corrida de 48 dígitos
// só vira linha digitável se FECHAR os cinco dígitos verificadores do convênio
// (`conferirCodigo`). Lixo não fecha verificador. Por isso o modo de falha
// deste arquivo é SILÊNCIO — a tela volta a dizer "copie à mão" —, nunca um
// número errado gravado como se fosse lido.
//
// O que é âncora e o que é enfeite: sem a linha digitável não se preenche
// NADA. Data e valor soltos não viram conta.
// =============================================================================
import { conferirCodigo, dadosDoCodigo, soDigitos } from './codigoBarras';
import { paraNumero } from './formato';

/** O que se conseguiu ler. Campo que não fechou vem nulo — nunca chutado. */
export interface CamposDoDas {
  /** 48 dígitos, verificadores conferidos. Sem ela nada mais vale. */
  linha: string;
  /** Do próprio código quando ele carrega valor (identificador 6 ou 8), senão
   *  do rótulo "Valor Total do Documento". Nulo quando nenhum dos dois fecha. */
  valor: number | null;
  /** 'aaaa-mm-dd'. O código de convênio NÃO carrega data — este campo só existe
   *  no texto, e é por isso que o leitor de barras sozinho nunca bastaria. */
  vencimento: string | null;
  /** "DAS setembro/2026 — parcela 7/60". Vazia quando não dá para montar. */
  descricao: string;
  /** Por que a folha NÃO pode entrar no cadastro em bloco, quando for o caso.
   *  Vazio = pode. A tela usa isto para mandar ao formulário em vez do lote. */
  soNoFormulario: string;
}

/** WinAnsi/Latin-1 dobrado para ASCII: os rótulos são escritos sem acento, para
 *  a busca não depender de "até", "Período" ou "Apuração" virem íntegros. */
export function semAcento(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * A SOPA: os literais de texto de um stream de conteúdo, na ordem do arquivo.
 *
 * A regra de junção tem duas linhas e mata os dois modos de falha de uma vez:
 *
 *   * fragmentos separados só por número de kerning colam SEM espaço — é o que
 *     devolve "1.234,56" de `[(1.2) 3 (34,) -2 (56)] TJ`, que senão viraria
 *     três pedaços e nenhum padrão casaria;
 *   * qualquer outro intervalo vira quebra de linha — senão textos vizinhos que
 *     não têm relação nenhuma se colam e inventam uma data dentro de um número.
 */
export function sopaDeConteudo(conteudo: string): string {
  const pedacos: string[] = [];
  let i = 0;
  let fimAnterior = -1;
  while (i < conteudo.length) {
    if (conteudo[i] !== '(') { i += 1; continue; }
    // Entre o literal anterior e este: só kerning (número, sinal, espaço)?
    if (fimAnterior >= 0) {
      const meio = conteudo.slice(fimAnterior, i);
      pedacos.push(/^[\s\d.\-+]*$/.test(meio) ? '' : '\n');
    }
    i += 1;
    let nivel = 1;
    let texto = '';
    while (i < conteudo.length && nivel > 0) {
      const c = conteudo[i]!;
      if (c === '\\') {
        const p = conteudo[i + 1] ?? '';
        if (p >= '0' && p <= '7') {
          // \ddd octal, até três dígitos.
          let oct = '';
          let k = i + 1;
          while (k < conteudo.length && oct.length < 3 && conteudo[k]! >= '0' && conteudo[k]! <= '7') {
            oct += conteudo[k]; k += 1;
          }
          texto += String.fromCharCode(parseInt(oct, 8));
          i = k;
          continue;
        }
        texto += ({ n: '\n', r: '\r', t: '\t', b: '\b', f: '\f' } as Record<string, string>)[p] ?? p;
        i += 2;
        continue;
      }
      if (c === '(') { nivel += 1; texto += c; i += 1; continue; }
      if (c === ')') { nivel -= 1; if (nivel > 0) texto += c; i += 1; continue; }
      texto += c;
      i += 1;
    }
    pedacos.push(texto);
    fimAnterior = i;
  }
  return pedacos.join('');
}

/**
 * TODA linha digitável de convênio que a sopa contiver, sem repetição.
 *
 * A geração é generosa de propósito — qualquer corrida de dígitos, pontuada ou
 * não, e TODA janela de 48 dentro dela — porque quem filtra é o dígito
 * verificador, não a forma. O DAS traz a mesma linha duas vezes (corpo e
 * canhoto) e as duas produzem a mesma string, que o Set resolve sozinho.
 *
 * Só 48/convênio: o boleto de 47 tem cadeia de verificação bem mais fraca, e
 * aceitar os dois aqui trocaria silêncio por falso positivo. PDF de banco é
 * outra decisão, com outros rótulos.
 */
export function linhasDigitaveis(sopa: string): string[] {
  const achadas = new Set<string>();
  for (const corrida of sopa.match(/[\d \n.\-]{48,}/g) ?? []) {
    const d = soDigitos(corrida);
    for (let i = 0; i + 48 <= d.length; i += 1) {
      const janela = d.slice(i, i + 48);
      if (janela.charAt(0) !== '8') continue;
      const c = conferirCodigo(janela);
      if (c.ok && c.tipo === 'convenio') achadas.add(janela);
    }
  }
  return [...achadas];
}

/** Os trechos que vêm logo DEPOIS de cada rótulo. É o que substitui a leitura
 *  de coordenada: no papel o valor fica ao lado do rótulo, e na sopa ele fica
 *  ao lado também — o gerador escreve na ordem em que desenha. */
function janelasAposRotulos(sopaSemAcento: string, rotulos: string[], tamanho: number): string[] {
  const j: string[] = [];
  for (const rotulo of rotulos) {
    let de = 0;
    for (;;) {
      const p = sopaSemAcento.indexOf(rotulo, de);
      if (p < 0) break;
      j.push(sopaSemAcento.slice(p + rotulo.length, p + rotulo.length + tamanho));
      de = p + rotulo.length;
    }
  }
  return j;
}

const DATA = /(?<!\d)([0-3]\d)\/(0\d|1[0-2])\/(20\d{2})(?!\d)/g;
/** A vírgula é EXIGIDA: é ela que descarta "Versão: 1.0.3" e o código de quatro
 *  dígitos da tabela de composição sem precisar de lista de exceção. */
const DINHEIRO = /(?<![\d,])(\d{1,3}(?:\.\d{3})*|\d+),(\d{2})(?![\d,])/g;

/** Data que existe no calendário, em 'aaaa-mm-dd'. Montada à mão: `new Date` de
 *  string tem fuso, e 31/11 viraria 01/12 em silêncio. */
function dataValida(dd: string, mm: string, aaaa: string): string | null {
  const ano = Number(aaaa);
  if (ano < 2020 || ano > 2099) return null;
  const d = new Date(Date.UTC(ano, Number(mm) - 1, Number(dd)));
  if (d.getUTCDate() !== Number(dd) || d.getUTCMonth() !== Number(mm) - 1) return null;
  return `${aaaa}-${mm}-${dd}`;
}

const distintas = (xs: string[]) => [...new Set(xs)];

const ROTULOS_VENC = ['pagar este documento', 'data de vencimento', 'vencimento'];
/** Rótulos de data que NÃO são vencimento. Um DAS tem várias datas, e emissão
 *  lançada como vencimento faria a conta nascer vencida. */
const ROTULOS_NAO_VENC = ['emissao', 'apuracao', 'transmissao', 'calculo', 'impressao'];

/**
 * O vencimento, em três degraus — e é o degrau que decide se a folha pode
 * entrar no cadastro em bloco ou só no formulário.
 *
 *   a) exatamente uma data distinta na janela dos rótulos → é ela, com rótulo;
 *   b) duas ou mais → VAZIO. Não se sorteia data de vencimento;
 *   c) nenhuma por rótulo, mas o arquivo inteiro só tem uma, e ela não está
 *      encostada num rótulo que diz que ela NÃO é vencimento → preenche, mas a
 *      folha não entra no bloco: o dono vê a data no formulário antes de gravar.
 */
export function vencimentoDoTexto(sopa: string): { iso: string | null; comRotulo: boolean } {
  const s = semAcento(sopa).toLowerCase();
  const daJanela: string[] = [];
  for (const janela of janelasAposRotulos(s, ROTULOS_VENC, 60)) {
    for (const m of janela.matchAll(DATA)) {
      const iso = dataValida(m[1]!, m[2]!, m[3]!);
      if (iso) daJanela.push(iso);
    }
  }
  const comRotulo = distintas(daJanela);
  if (comRotulo.length === 1) return { iso: comRotulo[0]!, comRotulo: true };
  if (comRotulo.length > 1) return { iso: null, comRotulo: false };

  const negativas = new Set<string>();
  for (const janela of janelasAposRotulos(s, ROTULOS_NAO_VENC, 60)) {
    for (const m of janela.matchAll(DATA)) {
      const iso = dataValida(m[1]!, m[2]!, m[3]!);
      if (iso) negativas.add(iso);
    }
  }
  const todas = distintas(
    [...s.matchAll(DATA)].map((m) => dataValida(m[1]!, m[2]!, m[3]!)).filter((x): x is string => !!x),
  ).filter((iso) => !negativas.has(iso));
  if (todas.length === 1) return { iso: todas[0]!, comRotulo: false };
  return { iso: null, comRotulo: false };
}

const ROTULO_VALOR = ['valor total do documento'];

/**
 * O valor impresso, do rótulo "Valor Total do Documento" e SÓ dele.
 *
 * "Totais" não serve de rótulo e "o maior valor" não serve de desempate: num
 * DAS medido, "Totais" devolveu o total da COLUNA Principal, não o do
 * documento. Quem usasse qualquer um dos dois lançaria a conta a MENOS.
 */
export function valorDoTexto(sopa: string): number | null {
  const s = semAcento(sopa).toLowerCase();
  const achados: number[] = [];
  for (const janela of janelasAposRotulos(s, ROTULO_VALOR, 40)) {
    for (const m of janela.matchAll(DINHEIRO)) {
      const v = paraNumero(m[0]!);
      if (v != null && v > 0) achados.push(v);
    }
  }
  const unicos = [...new Set(achados)];
  return unicos.length === 1 ? unicos[0]! : null;
}

const MESES = 'janeiro|fevereiro|marco|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro';

/**
 * "DAS setembro/2026 — parcela 7/60".
 *
 * Nunca "órgão do governo" (que é o que o segmento do código devolve, e seria o
 * mesmo defeito de nomear conta com "Bradesco"), nunca o CNPJ nem a razão
 * social: é a própria empresa do dono, não descreve documento nenhum.
 *
 * Sem o período, a descrição sai VAZIA: é ele que distingue um DAS do outro
 * dentro do ano, e "DAS" sozinho vira três contas iguais na lista.
 */
export function descricaoDoDas(sopa: string): string {
  const s = semAcento(sopa).toLowerCase();
  const ehDas = /arrecadacao do simples/.test(s) || /\bdas\b/.test(s);
  if (!ehDas) return '';
  const periodo = s.match(new RegExp(`(${MESES})\\/(20\\d{2})`));
  if (!periodo) return '';
  const parcela = s.match(/parcela:?\s*(\d{1,3})\/(\d{1,3})/);
  const mes = periodo[1]!.charAt(0).toUpperCase() + periodo[1]!.slice(1);
  const base = `DAS ${mes}/${periodo[2]}`;
  return parcela ? `${base} — parcela ${parcela[1]}/${parcela[2]}` : base;
}

/**
 * Tudo junto. Nulo quando não há âncora: zero linhas que fecham, ou DUAS —
 * duas é PDF com mais de um documento, e casar o valor de um com a data do
 * outro é pior que não ler nada.
 */
export function camposDoDas(sopa: string, hojeIso: string): CamposDoDas | { erro: string } {
  const linhas = linhasDigitaveis(sopa);
  if (linhas.length === 0) return { erro: 'sem-linha' };
  if (linhas.length > 1) {
    return { erro: `Achei ${linhas.length} documentos neste PDF. Mande um por vez.` };
  }
  const linha = linhas[0]!;
  const doCodigo = dadosDoCodigo(linha, hojeIso);
  const impresso = valorDoTexto(sopa);
  const venc = vencimentoDoTexto(sopa);
  const descricao = descricaoDoDas(sopa);

  // O valor do CÓDIGO manda quando existe: ele está coberto pelo dígito
  // verificador, e a minha regex não tem autoridade para contradizê-lo. O
  // impresso vira CONFERÊNCIA, não decisão.
  const valor = doCodigo.valor ?? impresso;
  const conferiu = doCodigo.valor == null || impresso == null
    ? doCodigo.valor == null
    : Math.abs(doCodigo.valor - impresso) < 0.005;

  const porques: string[] = [];
  if (doCodigo.valor != null && impresso != null && !conferiu) porques.push('confira o valor');
  if (venc.iso != null && !venc.comRotulo) porques.push('confira a data');
  if (!descricao) porques.push('dê um nome à conta');

  return {
    linha,
    valor: valor != null && valor > 0 ? valor : null,
    vencimento: venc.iso,
    descricao,
    soNoFormulario: porques.join(', '),
  };
}
